import React from "react";
import {
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  makeStyles,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
  Button
} from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "auto",
    maxWidth: 640
  },
  section: {
    marginBottom: theme.spacing(3)
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: theme.spacing(1)
  },
  keywordRow: {
    display: "flex",
    gap: 8,
    marginTop: theme.spacing(1),
    alignItems: "flex-start"
  },
  priority: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    background: theme.palette.action.hover,
    borderRadius: 4,
    fontSize: 13,
    color: theme.palette.text.secondary
  }
}));

const FlowTriggersPanel = ({
  triggerType,
  setTriggerType,
  triggerKeywords,
  setTriggerKeywords,
  whatsappId,
  setWhatsappId,
  whatsapps,
  triggerOncePerChat,
  setTriggerOncePerChat,
  triggersEnabled,
  setTriggersEnabled
}) => {
  const classes = useStyles();
  const [draft, setDraft] = React.useState("");

  const keywords = Array.isArray(triggerKeywords) ? triggerKeywords : [];

  const addKeyword = () => {
    const value = (draft || "").trim();
    if (!value) return;
    if (keywords.some(k => k.toLowerCase() === value.toLowerCase())) {
      setDraft("");
      return;
    }
    setTriggerKeywords([...keywords, value]);
    setDraft("");
  };

  const removeKeyword = idx => {
    const next = keywords.slice();
    next.splice(idx, 1);
    setTriggerKeywords(next);
  };

  return (
    <div className={classes.root}>
      <Typography variant="h6" gutterBottom>
        {i18n.t("chatFlows.triggers.title")}
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        {i18n.t("chatFlows.triggers.hint")}
      </Typography>

      <div className={classes.section}>
        <FormControlLabel
          style={{ display: "flex" }}
          control={
            <Switch
              checked={triggersEnabled !== false}
              onChange={e => setTriggersEnabled(e.target.checked)}
              color="primary"
            />
          }
          label={i18n.t("chatFlows.triggers.enabled")}
        />
        <FormHelperText>
          {i18n.t("chatFlows.triggers.enabledHelp")}
        </FormHelperText>
      </div>

      <div className={classes.section}>
        <FormControl component="fieldset" disabled={triggersEnabled === false}>
          <FormLabel component="legend">
            {i18n.t("chatFlows.triggers.mode")}
          </FormLabel>
          <RadioGroup
            value={triggerType || "any"}
            onChange={e => {
              const value = e.target.value;
              setTriggerType(value);
            }}
          >
            <FormControlLabel
              value="any"
              control={<Radio color="primary" />}
              label={i18n.t("chatFlows.triggers.anyMessage")}
            />
            <FormControlLabel
              value="keyword"
              control={<Radio color="primary" />}
              label={i18n.t("chatFlows.triggers.contains")}
            />
            <FormControlLabel
              value="exact"
              control={<Radio color="primary" />}
              label={i18n.t("chatFlows.triggers.exact")}
            />
          </RadioGroup>
          <FormHelperText>
            {i18n.t("chatFlows.triggers.modeHelp")}
          </FormHelperText>
        </FormControl>
        <FormControlLabel
          style={{ marginTop: 12, display: "flex" }}
          control={
            <Switch
              checked={Boolean(triggerOncePerChat)}
              onChange={e => setTriggerOncePerChat(e.target.checked)}
              color="primary"
              disabled={triggersEnabled === false}
            />
          }
          label={i18n.t("chatFlows.triggers.oncePerChat")}
        />
        <FormHelperText>
          {i18n.t("chatFlows.triggers.oncePerChatHelp")}
        </FormHelperText>
      </div>

      {(triggerType === "keyword" || triggerType === "exact") && (
        <div className={classes.section}>
          <Typography variant="subtitle2">
            {i18n.t("chatFlows.triggers.phrases")}
          </Typography>
          <div className={classes.keywordRow}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              value={draft}
              onChange={e => {
                const value = e.target.value;
                setDraft(value);
              }}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addKeyword();
                }
              }}
              placeholder={i18n.t("chatFlows.triggers.phrasePlaceholder")}
            />
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Add />}
              onClick={addKeyword}
            >
              {i18n.t("chatFlows.triggers.addPhrase")}
            </Button>
          </div>
          <div className={classes.chips}>
            {keywords.map((k, idx) => (
              <Chip
                key={`${k}-${idx}`}
                label={k}
                onDelete={() => removeKeyword(idx)}
                color="primary"
                variant="outlined"
              />
            ))}
          </div>
        </div>
      )}

      <div className={classes.section}>
        <TextField
          select
          label={i18n.t("chatFlows.triggers.whatsapp")}
          value={whatsappId == null ? "" : String(whatsappId)}
          onChange={e => {
            const value = e.target.value;
            setWhatsappId(value === "" ? null : Number(value));
          }}
          variant="outlined"
          size="small"
          fullWidth
          helperText={i18n.t("chatFlows.triggers.whatsappHelp")}
        >
          <MenuItem value="">
            {i18n.t("chatFlows.triggers.anyWhatsapp")}
          </MenuItem>
          {(whatsapps || []).map(w => (
            <MenuItem key={w.id} value={String(w.id)}>
              {w.name || w.id}
            </MenuItem>
          ))}
        </TextField>
      </div>

      <div className={classes.priority}>
        {i18n.t("chatFlows.triggers.priorityNote")}
      </div>
    </div>
  );
};

export default FlowTriggersPanel;
